import styled from '@emotion/styled';
import { useSnackbar } from 'notistack';
import type { CSSProperties, DragEvent, FunctionComponent } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChevronDownSmallLight, IconFile, IconFolder, IconUpload } from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';

const FileDropZoneLabel = styled.p(({ theme }) => ({
    color: 'var(--color-text-muted)',
    fontSize: theme.typography.body.S.medium.fontSize,
    margin: 0,
    textAlign: 'center',
}));

const StyledUploadIcon = styled(IconUpload)(({ theme }) => ({
    color: 'var(--color-text-disabled)',
    height: theme.spacing['8xl'],
    width: theme.spacing['8xl'],
}));

const Important = styled.span(({ theme }) => ({
    color: 'var(--color-text-heading)',
    fontSize: theme.typography.body.S.medium.fontSize,
    fontWeight: theme.typography.body.S.medium.fontWeight,
    lineHeight: theme.typography.body.S.medium.lineHeight,
}));

const FileDropZoneContainer = styled.div<{ readonly isDragActive: boolean }>(({ isDragActive, theme }) => {
    return {
        alignItems: 'center',
        border: `1.5px dashed ${isDragActive ? 'var(--color-border-drop-active)' : 'var(--color-border-subtle)'}`,
        borderRadius: theme.borderRadius.m,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.l,
        justifyContent: 'center',
        marginBottom: theme.spacing['4xl'],
        marginTop: theme.spacing['4xl'],
        padding: theme.spacing['4xl'],
    };
});

const HiddenInput = styled.input({
    display: 'none',
});

const DropZoneContent = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.l,
}));

const SupportedFormats = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    marginBottom: theme.spacing.m,
}));

const InlineGroups = styled.div(({ theme }) => ({
    alignItems: 'center',
    columnGap: theme.spacing.xxl,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: theme.spacing.s,
}));

const InlineGroup = styled.span(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.xs,
}));

const GroupLabel = styled.span(({ theme }) => ({
    color: 'var(--color-text-muted)',
    fontSize: theme.typography.body.S.medium.fontSize,
    fontWeight: theme.typography.body.S.medium.fontWeight,
    lineHeight: theme.typography.body.S.medium.lineHeight,
}));

const ExtensionPill = styled.span(({ theme }) => ({
    backgroundColor: 'var(--color-surface-subtle)',
    border: '1px solid var(--color-border-subtle)',
    borderRadius: theme.borderRadius.s,
    color: 'var(--color-text-muted)',
    fontSize: theme.typography.body.XS.regular.fontSize,
    lineHeight: theme.typography.body.XS.regular.lineHeight,
    padding: `${theme.spacing.xxs} ${theme.spacing.xs}`,
}));

const ButtonContent = styled.span(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

type FileDropZoneProps = {
    readonly accept?: string;
    readonly allowFolderUpload?: boolean;
    readonly isUploadInProgress?: boolean;
    readonly multiple?: boolean;
    readonly onSelectingFiles: (files: ReadonlyMap<string, File>) => Promise<void> | void;
    readonly style?: CSSProperties;
};

const ALLOWED_EXTENSION_GROUPS = {
    documents: ['pdf', 'doc', 'docx'],
    spreadsheets: ['xls', 'xlsx', 'csv'],
    text: ['txt', 'md', 'html'],
} as const;

const ALLOWED_EXTENSIONS: readonly string[] = Object.values(ALLOWED_EXTENSION_GROUPS).flat();

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES_PER_BATCH_UPLOAD_COUNT = 500;
/** The backend rejects the whole batch if any file name (the folder-relative path) is longer than this. */
const MAX_FILE_PATH_LENGTH = 255;

const getEffectiveFileName = (file: File): string => {
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;

    if (relativePath !== undefined && relativePath.length > 0) {
        const pathParts = relativePath.split('/');
        const pathWithoutRoot = pathParts.slice(1).join('/');

        return pathWithoutRoot.length > 0 ? pathWithoutRoot : file.name;
    }

    return file.name;
};

const filesToMap = (files: readonly File[]): ReadonlyMap<string, File> => {
    const map = new Map<string, File>();
    for (const file of files) {
        map.set(getEffectiveFileName(file), file);
    }

    return map;
};

export const FileDropZone: FunctionComponent<FileDropZoneProps> = ({
    accept = ALLOWED_EXTENSIONS.map((it) => `.${it}`).join(','),
    allowFolderUpload = true,
    isUploadInProgress,
    multiple = true,
    onSelectingFiles: onFilesSelected,
    style,
}) => {
    const { translate } = useContext(ApplicationContext);
    const { enqueueSnackbar } = useSnackbar();
    const [isDragActive, setIsDragActive] = useState(false);
    const groups = useMemo(
        () => [
            {
                extensions: ALLOWED_EXTENSION_GROUPS.documents,
                key: 'documents',
                label: translate('pages.job-architecture-v2.knowledge-base.file-group-documents'),
            },
            {
                extensions: ALLOWED_EXTENSION_GROUPS.spreadsheets,
                key: 'spreadsheets',
                label: translate('pages.job-architecture-v2.knowledge-base.file-group-spreadsheets'),
            },
            {
                extensions: ALLOWED_EXTENSION_GROUPS.text,
                key: 'text',
                label: translate('pages.job-architecture-v2.knowledge-base.file-group-text'),
            },
        ],
        [translate],
    );
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (folderInputRef.current !== null) {
            folderInputRef.current.setAttribute('webkitdirectory', '');
            folderInputRef.current.setAttribute('directory', '');
        }
    }, []);

    const isValidFilesLength = useCallback(
        (files: readonly File[]): boolean => {
            if (files.length > MAX_FILES_PER_BATCH_UPLOAD_COUNT) {
                enqueueSnackbar(
                    translate(
                        'pages.job-architecture-v2.knowledge-base.max-number-of-files-per-batch-exceeded-exception',
                        { count: MAX_FILES_PER_BATCH_UPLOAD_COUNT },
                    ),
                    { variant: 'error' },
                );

                return false;
            }

            return files.length > 0;
        },
        [enqueueSnackbar, translate],
    );

    const filterValidFiles = useCallback(
        (files: readonly File[]): File[] => {
            const oversizedFile = files.find((file) => file.size > MAX_FILE_SIZE_BYTES);
            if (oversizedFile !== undefined) {
                enqueueSnackbar(
                    translate('pages.job-architecture-v2.knowledge-base.file-too-large-exception', {
                        max_size: MAX_FILE_SIZE_MB,
                        name: oversizedFile.name,
                    }),
                    { variant: 'error' },
                );

                return [];
            }

            return files.filter((file) => {
                const fileName = file.name.toLowerCase();
                const lastDotIndex = fileName.lastIndexOf('.');

                if (lastDotIndex <= 0) {
                    return false;
                }

                const extension = fileName.slice(lastDotIndex + 1);

                return ALLOWED_EXTENSIONS.includes(extension);
            });
        },
        [enqueueSnackbar, translate],
    );

    const skipTooLongPaths = useCallback(
        (files: ReadonlyMap<string, File>): ReadonlyMap<string, File> => {
            const tooLong = [...files].filter(([path]) => path.length > MAX_FILE_PATH_LENGTH);
            if (tooLong.length === 0) {
                return files;
            }

            enqueueSnackbar(
                translate('pages.job-architecture-v2.knowledge-base.file-path-too-long-exception', {
                    max_length: MAX_FILE_PATH_LENGTH,
                    names: tooLong.map(([, file]) => file.name).join(', '),
                }),
                { variant: 'error' },
            );

            return new Map([...files].filter(([path]) => path.length <= MAX_FILE_PATH_LENGTH));
        },
        [enqueueSnackbar, translate],
    );

    const selectFiles = useCallback(
        (files: readonly File[]) => {
            if (!isValidFilesLength(files)) {
                return;
            }

            const filesToUpload = skipTooLongPaths(filesToMap(files));
            if (filesToUpload.size > 0) {
                onFilesSelected(filesToUpload);
            }
        },
        [isValidFilesLength, onFilesSelected, skipTooLongPaths],
    );

    const handleDragEnter = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (isUploadInProgress === true) {
                return;
            }
            setIsDragActive(true);
        },
        [isUploadInProgress],
    );

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragActive(false);

            if (isUploadInProgress === true) {
                return;
            }

            selectFiles(filterValidFiles(Array.from(e.dataTransfer.files)));
        },
        [filterValidFiles, isUploadInProgress, selectFiles],
    );

    const handleFilesClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFolderClick = useCallback(() => {
        folderInputRef.current?.click();
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (isUploadInProgress === true) {
                return;
            }

            selectFiles(filterValidFiles(Array.from(e.target.files ?? [])));
            e.target.value = '';
        },
        [filterValidFiles, isUploadInProgress, selectFiles],
    );

    return (
        <FileDropZoneContainer
            isDragActive={isDragActive}
            style={style}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {isUploadInProgress !== true && (
                <DropZoneContent>
                    <StyledUploadIcon />
                    <FileDropZoneLabel>
                        <Important>{translate('pages.job-architecture-v2.knowledge-base.drop-zone-label')}</Important>
                    </FileDropZoneLabel>
                    <SupportedFormats>
                        <InlineGroups>
                            {groups.map((group) => (
                                <InlineGroup key={group.key}>
                                    <GroupLabel>{group.label}</GroupLabel>
                                    {group.extensions.map((extension) => (
                                        <ExtensionPill key={extension}>.{extension}</ExtensionPill>
                                    ))}
                                </InlineGroup>
                            ))}
                        </InlineGroups>
                        <FileDropZoneLabel>
                            {translate('pages.job-architecture-v2.knowledge-base.drop-zone-max-file-size')}
                        </FileDropZoneLabel>
                    </SupportedFormats>
                    {allowFolderUpload ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger>
                                <Button size="sm" variant="primary">
                                    <ButtonContent>
                                        {translate('pages.job-architecture-v2.knowledge-base.upload-button')}
                                        <IconChevronDownSmallLight height={16} width={16} />
                                    </ButtonContent>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center" sideOffset={4}>
                                <DropdownMenuItem onSelect={handleFilesClick}>
                                    <IconFile height={16} width={16} />
                                    {translate('pages.job-architecture-v2.knowledge-base.files')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={handleFolderClick}>
                                    <IconFolder height={16} width={16} />
                                    {translate('pages.job-architecture-v2.knowledge-base.folder')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Button size="sm" variant="primary" onClick={handleFilesClick}>
                            {translate('pages.job-architecture-v2.knowledge-base.upload-button')}
                        </Button>
                    )}
                </DropZoneContent>
            )}
            {isUploadInProgress === true && (
                <FileDropZoneLabel>
                    <Spinner />
                </FileDropZoneLabel>
            )}
            <HiddenInput
                ref={fileInputRef}
                accept={accept}
                disabled={isUploadInProgress === true}
                multiple={multiple}
                type="file"
                onChange={handleInputChange}
            />
            {allowFolderUpload && (
                <HiddenInput
                    ref={folderInputRef}
                    accept={accept}
                    disabled={isUploadInProgress === true}
                    multiple={true}
                    type="file"
                    onChange={handleInputChange}
                />
            )}
        </FileDropZoneContainer>
    );
};
